import { Button, IconButton, List, ListItem, ListItemSecondaryAction, ListItemText, Typography, makeStyles, ListItemAvatar, Box, useTheme, useMediaQuery, CircularProgress } from "@material-ui/core";
import { Delete, DeleteForever, DeleteOutline, DeleteSweep, RemoveCircle, RemoveShoppingCart, ShoppingBasket, ShoppingCart } from '@material-ui/icons';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import Sheet from 'react-modal-sheet';
import { useRecoilState, useRecoilValue } from 'recoil';
import { localizeCurrency } from 'util/localizeFormat';
import { activeServiceState, bookingState, cartCachedState, cartState, cartVisibleState, categoriesState, clearCartState, isCartOpenState, packagesState, servicesState, sessionTokenState, stepState } from './state';
import { STEP_SERVICE_BREAKDOWN, STEP_SERVICE_DETAILS, STEP_SERVICE_PACKAGES } from './constants';
import { useTrackEvent } from 'util/trackEvent';
import { useHistory } from 'react-router-dom';
import { isAuthenticated, useUserState } from 'services/authentication.service';
import isEmpty from 'lodash.isempty';
import { red } from "@material-ui/core/colors";
import { isCartValidForService, isCartValidForSubservice } from "util/availability";
import { promoState } from "pages/CustomerBooking/RecoilState";
import { calculate, calculateFromBooking } from "services/createBooking.services";
import { useUpdateEffect } from "usehooks-ts";

const useStyles = makeStyles(theme => ({
  cartBar: {
    padding: theme.spacing(2),
    position: 'relative',
    '&>button': {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      margin: theme.spacing(2),
    },
    '&>ul': {
      marginBottom: theme.spacing(4)
    }
  },
  fabButton: {
    position: 'fixed',
    zIndex: theme.zIndex.drawer + 1,
    bottom: theme.spacing(1),
    left: theme.spacing(10),
    margin: '0 auto',
  },
  sheetContainer: {
    left: 0,
    right: 0,
    width: theme.breakpoints.values.sm
  }
}));

export const localPackageCalculate = ({ booking, services, packages }) => {
  const count = packages?.length + services?.length

  const packageTotal = packages?.reduce((acc, pkg) => {
    return acc + pkg?.fee
  }, 0)

  const serviceTotal = services?.reduce((acc, service) => {
    return acc + service?.fee
  }, 0)

  const total = packageTotal + serviceTotal + booking?.serviceCategory?.base_fee

  return {
    count,
    total
  }
}


const CartSuspenseButton = ({ onClick }) => {
  const cartCached = useRecoilValue(cartCachedState)
  return (
    <Button
      variant='contained'
      color='secondary'
      size="large"
      style={{
        boxShadow: 'none',
      }}
      onClick={() => onClick()}
      startIcon={<ShoppingCart />}
    >
      Checkout {localizeCurrency(cartCached?.total)}
    </Button>
  )
}

const Cart = ({ }) => {
  const classes = useStyles()
  const trackEvent = useTrackEvent()

  const [isOpen, setIsOpen] = useRecoilState(isCartOpenState)
  const [cartEnabled, setCartEnabled] = useRecoilState(cartVisibleState)

  const [cartValid, setCartValid] = useState(false);

  const [booking, setBooking] = useRecoilState(bookingState);
  const [packages, setPackages] = useRecoilState(packagesState);
  const [services, setServices] = useRecoilState(servicesState);

  const [cart, setCart] = useRecoilState(cartState);

  const [key, setKey] = useState(Math.random());
  const [step, setStep] = useRecoilState(stepState);
  const history = useHistory();
  const mappedCategories = useRecoilValue(categoriesState)
  const activeService = useRecoilValue(activeServiceState)
  const theme = useTheme()

  const [promo, setPromo] = useRecoilState(promoState);

  const { getUser } = useUserState()
  const [session_token, setSession_token] = useRecoilState(sessionTokenState);
  

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"),
    {
      noSsr: true,
    });

  useEffect(
    () => {
      const { isCartValid, markedCartServices, markedCartPackages } = isCartValidForService({
        activeService,
        categories: mappedCategories,
        cart: {
          services: services,
          packages: packages
        }
      })
      setCartValid(isCartValid)
      setPackages(markedCartPackages)
      setServices(markedCartServices)

      setCart({
        booking,
        services: markedCartServices,
        packages: markedCartPackages,
      })
    }, [activeService, mappedCategories, activeService, booking?.serviceCategory?.id]
  );



  // useEffect(
  //   () => {
  //     setKey(Math.random())
  //   }, [isOpen]
  // );


  return (
    <Sheet
      // key={key}
      detent='content-height'
      isOpen={isOpen}
      onClose={() => {
        trackEvent('Cart - Close Sheet')
        setIsOpen(false)
      }}
    >
      <Sheet.Container style={!isMobile && {
        position: 'absolute',
        marginLeft: 'auto',
        marginRight: 'auto',
        left: 0,
        right: 0,
        width: theme.breakpoints.values.sm,
        bottom: 0,
        margin: '0 auto'
      }}>
        <Sheet.Header />
        <Sheet.Content>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
          }}>
            <div style={{
              flexGrow: 1
            }} />
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DeleteSweep />}
              onClick={() => {
                trackEvent('Cart - Delete All in cart')
                setPackages([])
                setServices([])
                setIsOpen(false)
                setCart({
                  services: [],
                  packages: [],
                  booking: booking
                })
              }}>Remove All</Button>
          </div>
        </Sheet.Content>
        <Sheet.Content>
          <Sheet.Scroller
          draggableAt="both"
          >

            <div className={classes.cartBar}>
              <StandaloneCart />
            </div>
          </Sheet.Scroller>
        </Sheet.Content>
        <Sheet.Content style={{
          backgroundColor: cartValid ? theme.palette.secondary.main : theme.palette.background.paper
        }}>
          {cartValid ? (
            <Suspense 
              fallback={
              <Button 
                variant='text' 
                color='secondary' 
                size="large" 
                style={{
                  boxShadow: 'none',
                }}
                disabled
                startIcon={<CircularProgress size={24} />}
                >
                  Calculating...
                </Button>
              }>
                <CartSuspenseButton
                  onClick={() => {
                    setIsOpen(false)
                    trackEvent('Cart - Open Button')
                    setPromo(null)
                    setBooking({
                      ...booking,
                      promo: null
                    })
                    setStep(STEP_SERVICE_BREAKDOWN)
                    history.replace(STEP_SERVICE_BREAKDOWN, { internal: true })
                  }}                
                />
              </Suspense>
            
          ) : (
            <Button
              size="large"
              variant='text'
              color='secondary'
              disabled
              onClick={() => {
                trackEvent('Page Header Cart Button Clicked', { from: currentStep })
                setIsOpen(true)
              }}
              startIcon={<RemoveShoppingCart />}
              style={
                {
                  color: red[500]
                }
              }
            >Cart has invalid items</Button>
          )}
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop onTap={() => {
        trackEvent('Cart - Close Sheet By Backdrop')
        setIsOpen(false)
      }} />
    </Sheet>
  )
}

const StandaloneCart = ({ }) => {
  const classes = useStyles()
  const [booking, setBooking] = useRecoilState(bookingState);
  const [packages, setPackages] = useRecoilState(packagesState);
  const [services, setServices] = useRecoilState(servicesState);

  const [markedPackages, setMarkedPackages] = useState([]);
  const [markedServices, setMarkedServices] = useState([]);

  const [cart, setCart] = useRecoilState(cartState);

  const [displayPackages, setDisplayPackages] = useState();

  const [clearCart, setClearCart] = useRecoilState(clearCartState);

  const mappedCategories = useRecoilValue(categoriesState)
  const activeService = useRecoilValue(activeServiceState)

  const step = useRecoilValue(stepState)
  const trackEvent = useTrackEvent()


  useEffect(
    () => {
      if (step === STEP_SERVICE_PACKAGES) {
        const { isCartValid, markedCartServices, markedCartPackages } = isCartValidForService({
          activeService,
          categories: mappedCategories,
          cart: {
            services: services,
            packages: packages
          }
        })

        setMarkedPackages(markedCartPackages)
        setMarkedServices(markedCartServices)

        return isCartValid
      }
      else if (step === STEP_SERVICE_DETAILS) {
        const { isCartValid, markedCartServices, markedCartPackages } = isCartValidForSubservice({
          service: activeService,
          cart: {
            services: services,
            packages: packages
          }
        })

        setMarkedPackages(markedCartPackages)
        setMarkedServices(markedCartServices)

        return isCartValid
      }
    }, [packages, services, booking]
  );
  return (

    <List>
      {markedPackages?.map((pkg, index) => (
        <ListItem key={index}>
          <ListItemText
            primary={
              <Box style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div>
                  {pkg?.label} Package
                </div>
                {pkg?.markedAsAvailable ? null : <div> Not available in region</div>}
              </Box>
            }

            secondary={`${localizeCurrency(
              pkg?.fee || pkg?.fee_service
            )}`}
          />
          <ListItemSecondaryAction>
            <IconButton
              edge="end"
              aria-label="delete"
              onClick={() => {
                trackEvent('Cart - Remove Package', { package: pkg?.label })
                setPackages(markedPackages?.filter((p) => p?.id !== pkg?.id))
                setCart({
                  services: services,
                  packages: markedPackages?.filter((p) => p?.id !== pkg?.id),
                  booking: booking
                })
              }}
            >
              <Delete color='primary' />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      ))}
      {markedServices?.map((service, index) => {
        return (
          <ListItem key={index}>
            <ListItemText
              primary={
                <Box style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div>
                    {service?.label}
                  </div>
                  {service?.markedAsAvailable ? null : <div> Not available in region</div>}
                </Box>
              }
              secondary={`${localizeCurrency(service?.fee)}`}
            />
            <ListItemSecondaryAction>
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={() => {
                  trackEvent('Cart - Remove Service', { service: service?.label })
                  const finalServices = markedServices?.filter((s) => s?.id !== service?.id)
                  setServices(finalServices)
                  setCart({
                    services: finalServices,
                    packages: packages,
                    booking: booking
                  })
                  // if (finalServices?.length <= 0) {
                  //   setBooking({
                  //     ...booking,
                  //     serviceCategory: null
                  //   })
                  // }
                }}
              >
                <Delete color='primary' />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        )
      })}
      {(booking?.serviceCategory?.base_fee || booking?.serviceCategory?.fee_base) && (
        <ListItem>
          <ListItemText
            primary={`Service Charge`}
            secondary={`${localizeCurrency(booking?.serviceCategory?.base_fee || booking?.serviceCategory?.fee_base)}`}
          />
        </ListItem>
      )}
    </List>
  )
}

export { Cart, StandaloneCart }
